import React from 'react';
import PropTypes from 'prop-types';
import { useConfigService } from '@thd-nucleus/experience-context';
import {
  getSignInPath,
  isB2CUser,
  isLoggedIn,
} from '../../utils/general';
import { fetchAddressData, fetchProfileData } from '../apis/account';
import { NO, YES } from '../constants';
import { setupAnalytics } from '../analytics/common';

export const b2cPreferencePath = '/account/view/emailsubscription';
export const signInPath = getSignInPath();

function UserDataProvider({ children }) {

  const [loading, setLoading] = React.useState(true);

  const [profileData, setProfileData] = React.useState(null);
  const [addresses, setAddresses] = React.useState(null);
  const [managedAccount, setManagedAccount] = React.useState(NO);
  const [accountOwner, setAccountOwner] = React.useState(NO);

  const isNewB2cPageEnabled = useConfigService('ENABLE_NEW_B2C_PREFERENCE_LINK');
  const isTabsEnabled = useConfigService('tabs_enabled');
  const isOrderCommsEnabled = useConfigService('ordercomms_component_enabled');
  const isEReceiptEnabled = useConfigService('ereceipt_component_enabled');
  const isFccEnabled = useConfigService('fcc_consent_revocation_enabled');

  function redirectToSignIn() {
    window.location.replace(signInPath);
  }

  function formatAddress(address) {
    const postalDetails = address?.postalDetails;
    const nameDetails = address?.name;

    return {
      addressIdentifier: address?.addrIdentifier,
      isDefault: address?.isDefault,
      firstName: nameDetails?.firstName,
      lastName: nameDetails?.lastName,
      addressLine1: postalDetails?.addressLine1,
      addressLine2: postalDetails?.addressLine2, // todo: check if this is correct for address line2?
      city: postalDetails?.city,
      state: postalDetails?.state,
      zip5: postalDetails?.zipCode,
    };
  }

  function fetchProfile() {
    function error() {
      redirectToSignIn();
    }

    function success(res) {
      const profile = isB2CUser()
        ? res.data?.account?.profile
        : res?.data?.accountProfile?.userProfile;

      const phoneNumber = isB2CUser()
        ? profile?.existingPhoneNumber
        : profile?.phones?.find((phone) => phone?.primaryFlag === YES)?.number
        || '';

      const email = isB2CUser()
        ? profile?.emailId
        : profile?.emails?.find((emailAddr) => emailAddr?.primaryFlag === YES).emailAddress
        || '';

      const isB2B = !isB2CUser();

      setManagedAccount(res.data?.accountProfile?.customerProfile?.managedAccount);
      setAccountOwner(res.data?.accountProfile?.userProfile?.accountOwner);
      setProfileData({
        email,
        phoneNumber,
        managedAccount,
        isB2B
      });
    }

    fetchProfileData(success, error);
  }

  function fetchAddresses() {
    function error() {
      setAddresses([]);
    }

    function success(res) {
      const addressesList = res?.data?.addresses?.address;

      setAddresses(addressesList ? addressesList?.map(formatAddress) : []);
    }

    fetchAddressData(success, error);
  }

  React.useEffect(() => {
    if (profileData && addresses) {
      setLoading(false);
    }
  }, [profileData, addresses]);

  function setup() {
    if (!isLoggedIn()) {
      redirectToSignIn();
    } else if (!isNewB2cPageEnabled && isB2CUser()) {
      window.location.replace(b2cPreferencePath);
    } else {
      fetchAddresses();
      fetchProfile();
      setupAnalytics();
    }
  }

  React.useEffect(setup, []);

  function page() {
    return (
      <UserDataContext.Provider
        value={{
          phoneNumber: profileData.phoneNumber,
          email: profileData.email,
          addresses,
          managedAccount,
          isTabsEnabled,
          isEReceiptEnabled,
          isOrderCommsEnabled,
          isFccEnabled,
          isB2B: profileData.isB2B,
          accountOwner
        }}
      >
        {children}
      </UserDataContext.Provider>
    );
  }

  return (loading) ? (<>loading</>) : page();
}

const UserDataContext = React.createContext(null);

export { UserDataProvider, UserDataContext };

UserDataProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};
